/**
 * Config Registry UI Slice
 * Redux slice for Config Registry feature state management
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "store/Store";
import {
  ConfigRegistryItem,
  ConfigRegistryFilters,
  ConfigRegistryUIState,
  PaginationMeta,
  UISchemaResponse,
} from "../types/configRegistryTypes";

// =============================================================================
// INITIAL STATE
// =============================================================================

const defaultPagination: PaginationMeta = {
  page: 1,
  pageSize: 10,
  pageCount: 0,
  total: 0,
};

const defaultFilters: ConfigRegistryFilters = {
  configType: '',
  verificationStatus: '',
  approvalStatus: '', // Backward compatibility
  search: '',
  component: '',
  section: '',
  fileType: '',
  classification: '',
  restricted: 'all',
};

const initialState: ConfigRegistryUIState = {
  // List view state
  configs: [],
  pagination: defaultPagination,
  filters: defaultFilters,
  isLoading: false,
  error: undefined,
  
  // Detail/Edit view state
  currentConfig: null,
  isLoadingCurrent: false,
  
  // Lookup data (cached)
  uiSchema: null,
  configTypes: [],
  
  // UI state
  isFilterPanelOpen: false,
};

// =============================================================================
// SLICE
// =============================================================================

export const configRegistryUISlice = createSlice({
  name: 'configRegistryUI',
  initialState,
  reducers: {
    // List view actions
    setConfigs: (state, action: PayloadAction<ConfigRegistryItem[]>) => {
      state.configs = action.payload;
    },
    
    setPagination: (state, action: PayloadAction<PaginationMeta>) => {
      state.pagination = action.payload;
    },
    
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.page = 1; // Reset to first page
    },
    
    setFilters: (state, action: PayloadAction<Partial<ConfigRegistryFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to first page when filters change
    },
    
    resetFilters: (state) => {
      state.filters = defaultFilters;
      state.pagination.page = 1;
    },
    
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    
    // Detail view actions
    setCurrentConfig: (state, action: PayloadAction<ConfigRegistryItem | null>) => {
      state.currentConfig = action.payload;
    },
    
    setIsLoadingCurrent: (state, action: PayloadAction<boolean>) => {
      state.isLoadingCurrent = action.payload;
    },
    
    clearCurrentConfig: (state) => {
      state.currentConfig = null;
      state.isLoadingCurrent = false;
    },
    
    // Update current config status (after workflow action)
    updateCurrentConfigStatus: (state, action: PayloadAction<ConfigRegistryItem>) => {
      state.currentConfig = action.payload;
      // Also update in the list if present
      const index = state.configs.findIndex(c => c.documentId === action.payload.documentId);
      if (index !== -1) {
        state.configs[index] = action.payload;
      }
    },
    
    // Lookup data actions
    setUISchema: (state, action: PayloadAction<UISchemaResponse>) => {
      state.uiSchema = action.payload;
    },
    
    setConfigTypes: (state, action: PayloadAction<string[]>) => {
      state.configTypes = action.payload;
    },
    
    // UI state actions
    toggleFilterPanel: (state) => {
      state.isFilterPanelOpen = !state.isFilterPanelOpen;
    },
    
    setFilterPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isFilterPanelOpen = action.payload;
    },
    
    // Reset entire state
    resetConfigRegistryUI: () => initialState,
  },
});

// =============================================================================
// ACTIONS EXPORT
// =============================================================================

export const {
  setConfigs,
  setPagination,
  setPage,
  setPageSize,
  setFilters,
  resetFilters,
  setIsLoading,
  setError,
  setCurrentConfig,
  setIsLoadingCurrent,
  clearCurrentConfig,
  updateCurrentConfigStatus,
  setUISchema,
  setConfigTypes,
  toggleFilterPanel,
  setFilterPanelOpen,
  resetConfigRegistryUI,
} = configRegistryUISlice.actions;

// =============================================================================
// SELECTORS
// =============================================================================

// Helper to access the slice state from RootState with safety check
// Note: This slice needs to be added to Store.ts
const selectSlice = (state: RootState) =>
  (state as any).configRegistryUI as ConfigRegistryUIState | undefined;

// List view selectors (with fallbacks for safety)
export const selectConfigRegistryConfigs = (state: RootState) => selectSlice(state)?.configs ?? [];
export const selectConfigRegistryPagination = (state: RootState) => selectSlice(state)?.pagination ?? defaultPagination;
export const selectConfigRegistryFilters = (state: RootState) => selectSlice(state)?.filters ?? defaultFilters;
export const selectConfigRegistryIsLoading = (state: RootState) => selectSlice(state)?.isLoading ?? false;
export const selectConfigRegistryError = (state: RootState) => selectSlice(state)?.error;

// Detail view selectors
export const selectCurrentConfig = (state: RootState) => selectSlice(state)?.currentConfig ?? null;
export const selectIsLoadingCurrentConfig = (state: RootState) => selectSlice(state)?.isLoadingCurrent ?? false;

// Lookup data selectors
export const selectUISchema = (state: RootState) => selectSlice(state)?.uiSchema ?? null;
export const selectConfigTypes = (state: RootState) => selectSlice(state)?.configTypes ?? [];

// UI state selectors
export const selectIsFilterPanelOpen = (state: RootState) => selectSlice(state)?.isFilterPanelOpen ?? false;

// Computed selectors
export const selectHasActiveFilters = (state: RootState) => {
  const filters = selectSlice(state)?.filters;
  if (!filters) return false;
  return !!(
    filters.configType ||
    filters.verificationStatus ||
    filters.approvalStatus ||
    filters.search ||
    filters.component ||
    filters.section ||
    filters.fileType ||
    filters.classification ||
    (filters.restricted && filters.restricted !== 'all')
  );
};

